import axios from 'axios';
import {
  User,
  CreateUserRequest,
  UpdateUserRequest,
  UserFilters,
  UserListResponse,
  UserResponse,
  UserStatsResponse,
  ApiResponse,
  BulkOperationResponse
} from '../types';

const API_BASE_URL = '/api';

class UserService {
  private getAuthHeaders() {
    // Return axios-compatible headers object
    // NOTE: Authentication is handled via HTTP-only cookies (HIPAA-compliant)
    // The withCredentials: true option in axios calls sends the cookie
    return {
      'Content-Type': 'application/json'
    };
  }

  // Get all users with pagination and filters
  async getUsers(page: number = 1, limit: number = 10, filters: UserFilters = {}): Promise<UserListResponse> {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      
      // Add filters if they exist
      if (filters.role) params.append('role', filters.role);
      if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString());
      if (filters.search) params.append('search', filters.search);


      const response = await axios.get(`${API_BASE_URL}/user-management/users?${params}`, {
        headers: this.getAuthHeaders(),
        withCredentials: true // CRITICAL: Send HTTP-only auth cookie
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 403) {
          throw new Error('Access denied. Admin role required.');
        } else if (error.response?.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        }
      }
      throw new Error('Failed to fetch users');
    }
  }

  // Get user by ID
  async getUserById(id: string): Promise<UserResponse> {
    try {
      const response = await axios.get(`${API_BASE_URL}/user-management/users/${id}`, {
        headers: this.getAuthHeaders(),
        withCredentials: true
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching user:', error);
      throw new Error('Failed to fetch user');
    }
  }

  // Create new user
  async createUser(userData: CreateUserRequest): Promise<UserResponse> {
    try {
      // Capture the current browser URL for dynamic email links
      const browserUrl = window.location.origin;
      
      const response = await axios.post(`${API_BASE_URL}/user-management/users`, {
        ...userData,
        browserUrl: browserUrl
      }, {
        headers: this.getAuthHeaders(),
        withCredentials: true // CRITICAL: Send HTTP-only auth cookie
      });

      return response.data;
    } catch (error) {
      console.error('Error creating user:', error);
      if (axios.isAxiosError(error) && error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to create user');
    }
  }

  // Update user
  async updateUser(id: string, userData: UpdateUserRequest): Promise<UserResponse> {
    try {
      const response = await axios.put(`${API_BASE_URL}/user-management/users/${id}`, userData, {
        headers: this.getAuthHeaders(),
        withCredentials: true
      });

      return response.data;
    } catch (error) {
      console.error('Error updating user:', error);
      if (axios.isAxiosError(error) && error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to update user');
    }
  }

  // Delete user (soft delete)
  async deleteUser(id: string): Promise<ApiResponse> {
    try {
      const response = await axios.delete(`${API_BASE_URL}/user-management/users/${id}`, {
        headers: this.getAuthHeaders(),
        withCredentials: true
      });

      return response.data;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw new Error('Failed to delete user');
    }
  }

  // Hard delete user
  async hardDeleteUser(id: string): Promise<ApiResponse> {
    try {
      const response = await axios.delete(`${API_BASE_URL}/user-management/users/${id}/hard-delete`, {
        headers: this.getAuthHeaders(),
        withCredentials: true
      });

      return response.data;
    } catch (error) {
      console.error('Error hard deleting user:', error);
      throw new Error('Failed to permanently delete user');
    }
  }

  // Reactivate user
  async reactivateUser(id: string, reason?: string): Promise<ApiResponse> {
    try {
      // Capture the current browser URL for dynamic email links
      const browserUrl = window.location.origin;
      
      const response = await axios.post(`${API_BASE_URL}/account-management/reactivate/${id}`, {
        reason: reason || 'Manual reactivation by admin',
        browserUrl: browserUrl
      }, {
        headers: this.getAuthHeaders(),
        withCredentials: true
      });

      return response.data;
    } catch (error) {
      console.error('Error reactivating user:', error);
      throw new Error('Failed to reactivate user');
    }
  }

  // Reset user password
  async resetUserPassword(id: string): Promise<ApiResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/user-management/users/${id}/reset-password`, {}, {
        headers: this.getAuthHeaders(),
        withCredentials: true
      });

      return response.data;
    } catch (error) {
      console.error('Error resetting user password:', error);
      throw new Error('Failed to reset user password');
    }
  }

  // Get user statistics
  async getUserStats(): Promise<UserStatsResponse> {
    try {
      const response = await axios.get(`${API_BASE_URL}/user-management/users/stats/overview`, {
        headers: this.getAuthHeaders(),
        withCredentials: true
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching user stats:', error);
      throw new Error('Failed to fetch user statistics');
    }
  }

  // Bulk deactivate expired accounts
  async deactivateExpiredAccounts(): Promise<BulkOperationResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/user-management/users/bulk/deactivate-expired`, {}, {
        headers: this.getAuthHeaders(),
        withCredentials: true
      });

      return response.data;
    } catch (error) {
      console.error('Error deactivating expired accounts:', error);
      throw new Error('Failed to deactivate expired accounts');
    }
  }

  // Helper method to format date for display
  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  // Helper method to format date and time for display
  formatDateTime(dateString: string): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // Helper method to get user status badge
  getUserStatusBadge(user: User): { text: string; color: string } {
    if (!user.isActive) {
      return { text: 'inactive', color: 'red' };
    }
    
    if (user.isTempPasswordActive) {
      const expiryDate = new Date(user.tempPasswordExpiry);
      const now = new Date();
      
      if (expiryDate < now) {
        return { text: 'password expired', color: 'orange' };
      }
      
      const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysUntilExpiry <= 1) {
        return { text: 'expires soon', color: 'yellow' };
      }
      
      return { text: 'temp password', color: 'blue' };
    }
    
    return { text: 'active', color: 'green' };
  }

  // Helper method to get role display name
  getRoleDisplayName(role: string): string {
    const roleMap: Record<string, string> = {
      'admin': 'admin',
      'manufacturer': 'manufacturer',
      'practitioner': 'practitioner',
      'insurance': 'insurance'
    };
    
    return roleMap[role] || role;
  }

  // Helper method to get gender display name
  getGenderDisplayName(gender: string): string {
    const genderMap: Record<string, string> = {
      'male': 'Male',
      'female': 'Female',
      'other': 'Other',
      'prefer-not-to-say': 'Prefer not to say'
    };
    
    return genderMap[gender] || gender;
  }
}

export default new UserService();
